import { Add, Button, Chevron, Dropdown } from '@lmc/ui-kit'
import type { MenuProps } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { capitalize } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SHIFT, VISIT } from 'screens/Rostering/constants'
import { RotaPatternModal } from 'screens/Rostering/RotaPattern'
import NewRotaPatternModal from 'screens/Rostering/RotaPattern/NewRotaPatternModal/NewRotaPatternModal'
import type { RotaPatternModalType } from 'screens/Rostering/RotaPattern/utils'
import { commonPatterns } from 'utils/enhancedTestIds'

type AddRotaPatternDropdownProps = {
  testId?: string
  firstPattern?: boolean
}

export const AddRotaPatternDropdown = ({
  testId,
  firstPattern
}: AddRotaPatternDropdownProps) => {
  const { t } = useTranslation()
  const baseId = testId || 'add-rota-pattern-dropdown'

  const [patternModalType, setPatternModalType] = useState<
    RotaPatternModalType | undefined
  >(undefined)

  const flags = useFeatureFlags({
    flagNames: ['new_create_pattern', 'published_rota']
  })?.data

  const flagsNewCreatePatternEnabled = flags?.new_create_pattern?.enabled

  const flagsPublishedRotaEnabled = flags?.published_rota?.enabled

  const handleRotaMenuClick: MenuProps['onClick'] = (key) => {
    setPatternModalType(key as any)
  }

  const menuItems = [
    {
      key: VISIT,
      label: firstPattern ? t('ADD_A_VISIT') : capitalize(t('VISIT')),
      'data-intercom-target': 'add-rota-pattern-visit',
      'data-testid': commonPatterns.button(baseId, 'visit')
    },
    {
      key: SHIFT,
      label: firstPattern ? t('ADD_A_SHIFT') : capitalize(t('SHIFT')),
      'data-intercom-target': 'add-rota-pattern-shift',
      'data-testid': commonPatterns.button(baseId, 'shift')
    }
  ]

  return (
    <>
      <Dropdown
        data-testid={baseId}
        menu={{
          items: menuItems,
          onClick: ({ key }) => handleRotaMenuClick(key as any)
        }}
        trigger={['click']}
      >
        <span>
          <Button
            data-testid={commonPatterns.button(baseId, 'trigger')}
            variant={firstPattern ? 'primary' : 'default'}
            id="add-rota-pattern-dropdown"
            data-cy="btn-add-rota-pattern"
          >
            {flagsPublishedRotaEnabled && !firstPattern ? (
              <>
                <Add /> {t('ADD')}
              </>
            ) : (
              <>
                {t('ADD_ROTA_PATTERN')} <Chevron direction="down" />
              </>
            )}
          </Button>
        </span>
      </Dropdown>
      {!!patternModalType &&
        (flagsNewCreatePatternEnabled ? (
          <NewRotaPatternModal
            testId={commonPatterns.modal(baseId, patternModalType)}
            onCancel={() => setPatternModalType(undefined)}
            type={patternModalType as RotaPatternModalType}
          />
        ) : (
          <RotaPatternModal
            data-testid={commonPatterns.modal(baseId, patternModalType)}
            patternModalVisible={!!patternModalType}
            onCancel={() => setPatternModalType(undefined)}
            type={patternModalType as RotaPatternModalType}
          />
        ))}
    </>
  )
}
